import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { getCompanies, login } from '../../modules/auth';
import LoginForm from '../../components/login/LoginForm';
import {
  isNative,
  hasPromptedBefore,
  markPrompted,
  setBiometricEnabled,
  checkBiometryAvailable,
} from '../../lib/biometricLock';

const validationSchema = yup.object({
  username: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup
    .string('Enter your password')
    .min(4, 'Password should be of minimum 4 characters length')
    .required('Password is required'),
  company: yup.string('Select your company').required('Company is required'),
});

const LoginFormContainer = () => {
  const navigate = useNavigate();
  const [searchParams, setSeratchParams] = useSearchParams();
  const linkUrl = searchParams.get('linkurl') !== null && searchParams.get('linkurl') !== '' ? searchParams.get('linkurl') : '/';
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // 앱(네이티브)에서 로그인 성공 직후 "다음부터 지문으로 로그인할까요?" 1회 제안 - 2026-10-16.
  const [biometricOfferOpen, setBiometricOfferOpen] = useState(false);
  const { companies, authLogin, authLoginError, user } = useSelector(
    ({ auth }) => ({
      companies: auth.companies,
      authLogin: auth.authLogin,
      authLoginError: auth.authLoginError,
      user: auth.user,
    }),
  );

  //컴포넌트가 처음 렌더링될때 초기화함.
  useEffect(() => {
    dispatch(getCompanies());
  }, []);

  //로그인 성공/실패
  useEffect(() => {
    if (authLoginError) {
      let message =
        typeof authLoginError.response !== 'undefined'
          ? authLoginError.response.data.message
          : authLoginError.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }

    if (authLogin && user) {
      if (isNative() && !hasPromptedBefore()) {
        checkBiometryAvailable().then((info) => {
          if (info?.isAvailable) {
            setBiometricOfferOpen(true);
          } else {
            markPrompted();
            navigate(linkUrl);
          }
        });
      } else {
        navigate(linkUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLogin, authLoginError, user]);

  const closeBiometricOffer = (enable) => {
    markPrompted();
    setBiometricEnabled(enable);
    setBiometricOfferOpen(false);
    navigate(linkUrl);
  };

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      company: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      let dataToSubmit = {
        username: values.username,
        password: values.password,
        company: values.company,
        // 2026-10-16: 기기별 리프레시 세션 구분(SYS_USR_SESSION.DEVICE_TYPE) - 앱(Capacitor)인지
        // 웹브라우저인지로 리프레시 토큰 수명(90일 vs 30일)과 프로필의 "로그인된 기기" 표시가 갈린다.
        deviceType: isNative() ? 'android' : 'web',
      };

      setSubmitting(true);

      dispatch(login(dataToSubmit));

      formik.resetForm();
      setSubmitting(false);
    },
  });

  return (
    <>
      <LoginForm companies={companies} formik={formik} />
      <Dialog open={biometricOfferOpen} onClose={() => closeBiometricOffer(false)}>
        <DialogTitle>다음부터 지문으로 로그인할까요?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            지문/얼굴 인식(또는 기기 PIN/패턴)으로 빠르게 잠금을 해제할 수 있습니다. 나중에
            프로필 화면에서 언제든 끌 수 있습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeBiometricOffer(false)}>나중에</Button>
          <Button variant="contained" onClick={() => closeBiometricOffer(true)} autoFocus>
            사용
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginFormContainer;
